import React, { useState, useEffect } from 'react';
import { pointsAPI, configAPI } from '../../api';
import { formatDate } from '../../utils';
import { Card, Spinner, EmptyState } from '../../components/ui';
import { Award } from 'lucide-react';

export const BadgesPage = () => {
  const [badges, setBadges] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [badgesRes, configRes] = await Promise.all([
          pointsAPI.getBadges(),
          configAPI.get().catch(() => null)
        ]);
        setBadges(badgesRes.data);
        if (configRes) setConfig(configRes.data);
      } catch (err) {
        console.error('Failed to load badges:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const earnedBadgeIds = badges.map(b => b.badge_id);
  const allBadges = config?.badges || [];
  const unearnedBadges = allBadges.filter(b => !earnedBadgeIds.includes(b.id));

  if (loading) {
    return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="badges-page">
      <div>
        <h1 className="text-2xl font-bold">Badges</h1>
        <p className="text-gray-500">Your achievements and milestones</p>
      </div>

      {/* Earned Badges */}
      <div>
        <h3 className="font-semibold mb-4">Earned ({badges.length})</h3>
        {badges.length === 0 ? (
          <Card>
            <EmptyState
              icon={Award}
              title="No badges yet"
              description="Keep training to earn your first badge!"
            />
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {badges.map(badge => (
              <Card key={badge.id} className="p-4 text-center">
                <div className="text-4xl mb-2">{badge.badge_icon}</div>
                <h4 className="font-semibold">{badge.badge_name}</h4>
                <p className="text-xs text-gray-500 mt-1">{badge.badge_description}</p>
                <p className="text-xs text-primary-600 mt-2">Earned {formatDate(badge.earned_at)}</p>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Unearned Badges */}
      {unearnedBadges.length > 0 && (
        <div>
          <h3 className="font-semibold mb-4 text-gray-500">Available to Earn ({unearnedBadges.length})</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {unearnedBadges.map(badge => (
              <Card key={badge.id} className="p-4 text-center opacity-50 grayscale">
                <div className="text-4xl mb-2">{badge.icon}</div>
                <h4 className="font-semibold">{badge.name}</h4>
                <p className="text-xs text-gray-500 mt-1">{badge.description}</p>
                <p className="text-xs text-amber-600 mt-2">+{badge.points_bonus} bonus pts</p>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BadgesPage;
